import React from 'react';
import styles from './DataTable.module.css';

/**
 * Таблица списка с поиском, сортировкой и постраничной навигацией.
 *
 * Прежние таблицы были просто <Table> с заголовками ID / NAME / FILE / UPDATE / DELETE,
 * без состояния загрузки и пустого состояния: при пустом ответе экран выглядел
 * точно так же, как при незагруженном.
 *
 * @param columns [{ key, title, render(row), sortable, width, align }]
 */
const DataTable = ({
    columns,
    rows,
    loading,
    error,
    rowKey = (row) => row.id,
    // поиск
    search,
    onSearchChange,
    searchPlaceholder = 'Поиск по названию',
    // сортировка
    sort,
    dir,
    onSortChange,
    // пагинация
    page,
    perPage,
    total,
    lastPage,
    onPageChange,
    onPerPageChange,
    // дополнительное
    filters,
    actions,
    emptyText = 'Ничего не найдено',
    emptyHint,
    onRetry,
}) => {
    const hasRows = rows && rows.length > 0;

    return (
        <div className={`adm-card ${styles.wrapper}`}>
            <div className={styles.toolbar}>
                <div className={styles.searchBox}>
                    <span className={styles.searchIcon} aria-hidden="true">
                        ⌕
                    </span>
                    <input
                        type="search"
                        className={styles.search}
                        value={search}
                        onChange={(e) => onSearchChange(e.target.value)}
                        placeholder={searchPlaceholder}
                        aria-label={searchPlaceholder}
                    />
                    {search && (
                        <button
                            type="button"
                            className={styles.searchClear}
                            onClick={() => onSearchChange('')}
                            aria-label="Очистить поиск"
                        >
                            ×
                        </button>
                    )}
                </div>

                {filters && <div className={styles.filters}>{filters}</div>}
                {actions && <div className={styles.actions}>{actions}</div>}
            </div>

            <div className={styles.scroll}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            {columns.map((col) => {
                                const active = sort === col.key;
                                return (
                                    <th
                                        key={col.key}
                                        style={col.width ? { width: col.width } : undefined}
                                        className={col.align === 'right' ? styles.alignRight : undefined}
                                        aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : undefined}
                                    >
                                        {col.sortable && onSortChange ? (
                                            <button
                                                type="button"
                                                className={`${styles.sortButton} ${active ? styles.sortActive : ''}`}
                                                onClick={() => onSortChange(col.key)}
                                            >
                                                {col.title}
                                                <span className={styles.sortArrow}>
                                                    {active ? (dir === 'asc' ? '↑' : '↓') : '↕'}
                                                </span>
                                            </button>
                                        ) : (
                                            col.title
                                        )}
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>

                    <tbody>
                        {loading &&
                            Array.from({ length: Math.min(perPage || 5, 6) }).map((_, i) => (
                                <tr key={`skeleton-${i}`} className={styles.skeletonRow}>
                                    {columns.map((col) => (
                                        <td key={col.key}>
                                            <span className={styles.skeleton} />
                                        </td>
                                    ))}
                                </tr>
                            ))}

                        {!loading && error && (
                            <tr>
                                <td colSpan={columns.length} className={styles.stateCell}>
                                    <div className={styles.stateTitle}>Не удалось загрузить данные</div>
                                    <div className={styles.stateHint}>{error.message}</div>
                                    {onRetry && (
                                        <button
                                            type="button"
                                            className="adm-btn adm-btn-secondary adm-btn-sm"
                                            onClick={onRetry}
                                        >
                                            Повторить
                                        </button>
                                    )}
                                </td>
                            </tr>
                        )}

                        {!loading && !error && !hasRows && (
                            <tr>
                                <td colSpan={columns.length} className={styles.stateCell}>
                                    <div className={styles.stateTitle}>{emptyText}</div>
                                    {emptyHint && <div className={styles.stateHint}>{emptyHint}</div>}
                                </td>
                            </tr>
                        )}

                        {!loading &&
                            !error &&
                            hasRows &&
                            rows.map((row) => (
                                <tr key={rowKey(row)}>
                                    {columns.map((col) => (
                                        <td
                                            key={col.key}
                                            className={col.align === 'right' ? styles.alignRight : undefined}
                                        >
                                            {col.render ? col.render(row) : row[col.key]}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                    </tbody>
                </table>
            </div>

            <Pagination
                page={page}
                perPage={perPage}
                total={total}
                lastPage={lastPage}
                loading={loading}
                onPageChange={onPageChange}
                onPerPageChange={onPerPageChange}
            />
        </div>
    );
};

const Pagination = ({ page, perPage, total, lastPage, loading, onPageChange, onPerPageChange }) => {
    if (!total && !loading) return null;

    const from = total === 0 ? 0 : (page - 1) * perPage + 1;
    const to = Math.min(page * perPage, total);

    return (
        <div className={styles.pagination}>
            <div className={styles.paginationInfo}>
                {loading ? 'Загрузка…' : `${from}–${to} из ${total}`}
            </div>

            <div className={styles.paginationControls}>
                {onPerPageChange && (
                    <label className={styles.perPage}>
                        Строк:
                        <select
                            value={perPage}
                            onChange={(e) => onPerPageChange(Number(e.target.value))}
                            className={styles.perPageSelect}
                        >
                            {[20, 50, 100].map((n) => (
                                <option key={n} value={n}>
                                    {n}
                                </option>
                            ))}
                        </select>
                    </label>
                )}

                <button
                    type="button"
                    className="adm-btn adm-btn-secondary adm-btn-sm"
                    onClick={() => onPageChange(page - 1)}
                    disabled={page <= 1 || loading}
                >
                    ← Назад
                </button>
                <span className={styles.pageNumber}>
                    {page} / {lastPage || 1}
                </span>
                <button
                    type="button"
                    className="adm-btn adm-btn-secondary adm-btn-sm"
                    onClick={() => onPageChange(page + 1)}
                    disabled={page >= (lastPage || 1) || loading}
                >
                    Вперёд →
                </button>
            </div>
        </div>
    );
};

export default DataTable;
